import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "notifications-page" }
const _hoisted_2 = { class: "notifications-page__header" }
const _hoisted_3 = { class: "notifications-page__actions" }
const _hoisted_4 = {
  class: "notifications-page__tabs",
  role: "tablist"
}
const _hoisted_5 = ["aria-selected"]
const _hoisted_6 = ["aria-selected"]
const _hoisted_7 = {
  key: 0,
  class: "notifications-page__status notifications-page__status--error"
}
const _hoisted_8 = {
  key: 1,
  class: "notifications-page__status"
}
const _hoisted_9 = {
  key: 2,
  class: "notifications-page__status"
}
const _hoisted_10 = {
  key: 3,
  class: "notifications-page__list"
}
const _hoisted_11 = ["onClick"]
const _hoisted_12 = {
  class: "material-icons",
  "aria-hidden": "true"
}

import { onBeforeUnmount, ref, watch } from 'vue';
import type { Timestamp, Unsubscribe } from 'firebase/firestore';
import { Button } from '@/components/ui';
import { getCurrentUser } from '@/modules/firebase/Auth';
import {
  archiveNotifications,
  markNotificationRead,
  markNotificationsRead,
  subscribeNotifications,
  type UserNotification
} from '@/modules/notifications/NotificationClient';


export default /*@__PURE__*/_defineComponent({
  __name: 'NotificationsPage',
  setup(__props) {

const user = getCurrentUser();
const archived = ref(false);
const items = ref<UserNotification[]>([]);
const loading = ref(true);
const busy = ref(false);
const errorMessage = ref('');
let unsubscribe: Unsubscribe | null = null;

function load(): void {
  unsubscribe?.();
  items.value = [];
  loading.value = true;
  if (!user?.uid) return;
  unsubscribe = subscribeNotifications(user.uid, archived.value, notifications => {
    items.value = notifications;
    loading.value = false;
    errorMessage.value = '';
  }, error => {
    errorMessage.value = error.message;
    loading.value = false;
  });
}

async function mutate(action: () => Promise<unknown>): Promise<void> {
  busy.value = true;
  errorMessage.value = '';
  try { await action(); } catch (error) { errorMessage.value = error instanceof Error ? error.message : 'Notification action failed.'; }
  finally { busy.value = false; }
}

function markAllRead(): void { void mutate(() => markNotificationsRead({ scope: 'all' })); }
function clearRead(): void { void mutate(() => archiveNotifications({ scope: 'read' })); }
function clearAll(): void { void mutate(() => archiveNotifications({ scope: 'all' })); }

async function openNotification(notification: UserNotification): Promise<void> {
  if (!notification.readAt) await markNotificationRead(notification.id).catch(error => { errorMessage.value = error.message; });
  window.location.assign(notification.actionPath);
}

function formatTime(timestamp: Timestamp): string {
  return timestamp?.toDate ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(timestamp.toDate()) : '';
}

watch(archived, load, { immediate: true });
onBeforeUnmount(() => unsubscribe?.());

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("main", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _cache[5] || (_cache[5] = _createElementVNode("div", null, [
        _createElementVNode("p", null, "Inbox"),
        _createElementVNode("h1", null, "Notifications"),
        _createElementVNode("span", null, "Mentions, starred-shipment activity, and operational assignments.")
      ], -1 /* CACHED */)),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(Button), {
          variant: "soft",
          tone: "neutral",
          size: "sm",
          disabled: busy.value || archived.value,
          onClick: markAllRead
        }, {
          default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
            _createTextVNode("Mark all read", -1 /* CACHED */)
          ]))]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["disabled"]),
        _createVNode(_unref(Button), {
          variant: "soft",
          tone: "neutral",
          size: "sm",
          disabled: busy.value || archived.value,
          onClick: clearRead
        }, {
          default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
            _createTextVNode("Clear read", -1 /* CACHED */)
          ]))]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["disabled"]),
        _createVNode(_unref(Button), {
          variant: "soft",
          tone: "danger",
          size: "sm",
          disabled: busy.value || archived.value,
          onClick: clearAll
        }, {
          default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
            _createTextVNode("Clear all", -1 /* CACHED */)
          ]))]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["disabled"])
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("button", {
        type: "button",
        role: "tab",
        "aria-selected": !archived.value,
        class: _normalizeClass({ active: !archived.value }),
        onClick: _cache[0] || (_cache[0] = ($event: any) => (archived.value = false))
      }, "Active", 10 /* CLASS, PROPS */, _hoisted_5),
      _createElementVNode("button", {
        type: "button",
        role: "tab",
        "aria-selected": archived.value,
        class: _normalizeClass({ active: archived.value }),
        onClick: _cache[1] || (_cache[1] = ($event: any) => (archived.value = true))
      }, "Archived", 10 /* CLASS, PROPS */, _hoisted_6)
    ]),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(errorMessage.value), 1 /* TEXT */))
      : (loading.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_8, "Loading notifications…"))
        : (items.value.length === 0)
          ? (_openBlock(), _createElementBlock("p", _hoisted_9, "No " + _toDisplayString(archived.value ? 'archived' : 'active') + " notifications.", 1 /* TEXT */))
          : (_openBlock(), _createElementBlock("ul", _hoisted_10, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (notification) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: notification.id
                }, [
                  _createElementVNode("button", {
                    type: "button",
                    class: _normalizeClass({ unread: !notification.readAt }),
                    onClick: ($event: any) => (openNotification(notification))
                  }, [
                    _createElementVNode("i", _hoisted_12, _toDisplayString(notification.type === 'mention' ? 'alternate_email' : 'notifications'), 1 /* TEXT */),
                    _createElementVNode("span", null, [
                      _createElementVNode("strong", null, _toDisplayString(notification.title), 1 /* TEXT */),
                      _createElementVNode("small", null, _toDisplayString(notification.preview), 1 /* TEXT */),
                      _createElementVNode("time", null, _toDisplayString(formatTime(notification.createdAt)) + " · " + _toDisplayString(notification.subject.label), 1 /* TEXT */)
                    ])
                  ], 10 /* CLASS, PROPS */, _hoisted_11)
                ]))
              }), 128 /* KEYED_FRAGMENT */))
            ]))
  ]))
}
}

})